import { z } from 'zod';
import { VALIDATION_PATTERNS, ERROR_MESSAGES } from 'src/contstant/commonLabels';
import { isHtmlContentEmpty } from 'src/validations/translationFormSchema';

/**
 * Normalizes an incoming form value to either `undefined` (nothing entered)
 * or a `number` (a numeric input the schema can then range-check).
 * Non-numeric strings and NaN are passed through untouched so the schema's
 * `invalid_type_error` message is what the user sees.
 */
const preprocessDuration = (value: unknown): unknown => {
  if (value === '' || value === null || value === undefined) return undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return undefined;
    const n = Number(trimmed);
    return Number.isNaN(n) ? value : n;
  }
  return value;
};

/**
 * Zod validation schema for the TaskDraftForm component.
 * Validates editable form fields for task draft creation/editing.
 */
export const taskDraftFormSchema = z.object({
  taskName: z.string().min(1, { message: 'Task name is required' }),
  taskDurationDays: z.preprocess(
    preprocessDuration,
    z
      .number({
        // Zod v4 merged required_error and invalid_type_error into a single
        // error callback that inspects the offending input. `undefined` here
        // signals "nothing was entered" (see preprocessDuration above); any
        // other value that reaches this branch is a non-numeric type.
        error: (issue) =>
          issue.input === undefined
            ? 'Task duration is required'
            : 'Task duration must be a whole number',
      })
      .int({ message: 'Task duration must be a whole number' })
      .min(1, { message: 'Task duration must be at least 1 day' })
      .max(365, { message: 'Task duration must be at most 365 days' }),
  ),
  // allowImageOnly: the description comes from a Rich Text Editor and
  // may legitimately be a single pasted screenshot with no text.
  taskDescription: z
    .string()
    .refine((val) => !isHtmlContentEmpty(val, { allowImageOnly: true }), {
      message: 'Task description is required',
    }),
  assignee: z
    .string()
    .min(1, { message: 'Assignee is required' })
    .regex(VALIDATION_PATTERNS.ASSIGNEE_ALIAS, {
      message: ERROR_MESSAGES.ASSIGNEE_AT_SYMBOL,
    }),
  auditorObservation: z.string().optional().default(''),
});

/**
 * Form-state shape for TaskDraftForm.
 *
 * `taskDurationDays` is widened to `number | undefined` (vs. the schema's
 * inferred `number` output) so the "field starts blank" contract is captured
 * at the type level. Consumers that receive TaskDraftFormData after Zod
 * validation succeeds should narrow with a `typeof x === 'number'` guard
 * before treating it as a number — matching the pattern in CreateTaskForm.
 */
export type TaskDraftFormData = Omit<
  z.infer<typeof taskDraftFormSchema>,
  'taskDurationDays'
> & {
  taskDurationDays: number | undefined;
};
